/**
 * parallel-editor-v2 — the floating selection bubble bar. Pure chrome: renders from a plain
 * SelectionInfo snapshot and fires plain callbacks upward; no ProseMirror/Yjs, no portal/toast
 * libraries. Command buttons run on mousedown with preventDefault (NOT click) so the editor
 * selection is not blown away before the command executes.
 *
 * Two rows: row 1 = large primary actions (Commit · first-class AI buttons · Task/Remind) —
 * no dropdowns, every AI action is one visible violet button; row 2 = the compact format
 * cluster. Free-form AI prompts moved OUT of the bar into the PromptDock ("Ask AI" focuses it).
 */

import * as React from 'react';
import type { BlockType, InlineFormat, ListType, SelectionInfo } from '@docit/editor-core';

/**
 * A configured selection action (from the app's SelectionActionsStore — user/team/project/document
 * scoped, managed in AI Actions settings). The bar renders THESE, not a hardcoded list, so a user's
 * Compact / Explain / Expand / Formalize / … and any custom actions all appear with their prompts.
 */
export interface SelectionActionItem {
  id: string;
  title: string;
  prompt: string;
  icon?: string;
  shortcut?: string;
}

/** Shown only if the host has not yet loaded any configured actions — never leaves the bar bare. */
const FALLBACK_ACTIONS: ReadonlyArray<SelectionActionItem> = [
  {
    id: 'rewrite',
    title: 'Rewrite',
    prompt:
      'Rewrite this text to improve clarity, flow, and readability while preserving the original meaning.',
  },
];

/** How many configured actions render as first-class buttons before the rest fold into "More". */
const PRIMARY_ACTION_COUNT = 4;

export interface BubbleBarProps {
  /** Latest selection snapshot (parent only renders the bar when non-empty with a rect). */
  selection: SelectionInfo;
  /** True while an AI action is in flight — replaces the AI buttons with a same-width pill. */
  aiBusy: boolean;
  onCommit(): void;
  onToggleFormat(f: InlineFormat): void;
  onSetBlockType(b: BlockType): void;
  onToggleList(l: ListType): void;
  /** Configured selection actions to render as first-class AI buttons (+ a "More" overflow menu). */
  actions: SelectionActionItem[];
  /** Run an AI action over the current selection (the action's id as capability + its prompt). */
  onAIAction(capability: string, instruction: string): void;
  /** Free-form prompt entry point — focuses the PromptDock (no inline popover). */
  onAskAI(): void;
  /** Insert a task live-object seeded from the selection (local act, no round-trip). */
  onStartTask(): void;
  /** Taskize — extract tasks & decisions from the selection (host runs the review tray). */
  onTaskize(): void;
  /** Insert a reminder live-object. `due` is the raw datetime-local value. */
  onRemind(due: string, note: string): void;
  /** Return focus to the editor after closing a popover. */
  onFocusEditor(): void;
}

export function BubbleBar(props: BubbleBarProps): React.JSX.Element | null {
  const [remindOpen, setRemindOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [remindDue, setRemindDue] = React.useState('');
  const [remindNote, setRemindNote] = React.useState('');
  const barRef = React.useRef<HTMLDivElement | null>(null);
  const [barSize, setBarSize] = React.useState<{ w: number; h: number } | null>(null);
  const aiGroupRef = React.useRef<HTMLSpanElement | null>(null);
  const [aiGroupW, setAiGroupW] = React.useState<number | null>(null);

  // Measure the rendered bar so clamping/flipping accounts for its REAL size (translateX(-50%)
  // means half the width hangs to each side of the anchor). Equality-guarded set — no loop.
  React.useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    setBarSize((cur) => (cur && cur.w === w && cur.h === h ? cur : { w, h }));
  });

  // Measure the three AI buttons while visible so the busy pill can hold their exact width —
  // the centered bar must not shift every other button sideways mid-interaction.
  React.useLayoutEffect(() => {
    if (props.aiBusy) return; // keep the last measured width while the pill is showing
    const el = aiGroupRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    setAiGroupW((cur) => (cur === w ? cur : w));
  });

  // The popover opens via mousedown+preventDefault, so focus STAYS in the editor and keydown
  // never bubbles through the bar — Escape and click-outside are handled at the document level.
  const onFocusEditor = props.onFocusEditor;
  const anyPopoverOpen = remindOpen || moreOpen;
  React.useEffect(() => {
    if (!anyPopoverOpen) return undefined;
    const closeAll = () => {
      setRemindOpen(false);
      setMoreOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAll();
        onFocusEditor();
      }
    };
    const onMouseDown = (e: MouseEvent) => {
      const el = barRef.current;
      if (el && e.target instanceof Node && !el.contains(e.target)) {
        closeAll();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [anyPopoverOpen, onFocusEditor]);

  const sel = props.selection;
  const rect = sel.rect;
  if (!rect) return null;

  const openRemind = () => {
    setRemindDue('');
    setRemindNote(sel.text.slice(0, 80));
    setRemindOpen(true);
  };

  /**
   * mousedown + preventDefault so the editor selection survives pointer use; onClick with
   * `e.detail === 0` (a keyboard-generated click — Enter/Space) gives every command a keyboard
   * path without double-firing after a real mousedown.
   */
  const cmd = (fn: () => void) => ({
    onMouseDown: (e: React.MouseEvent) => {
      e.preventDefault();
      fn();
    },
    onClick: (e: React.MouseEvent) => {
      if (e.detail === 0) fn();
    },
  });

  const submitRemind = () => {
    if (!remindDue) return;
    const note = remindNote.trim() || sel.text.slice(0, 80);
    setRemindOpen(false);
    props.onRemind(remindDue, note);
  };

  // Fixed at the selection's viewport coords: 8px gap. The clamp works on the bar's EDGES
  // (anchor ± half the measured width, since translateX(-50%) centers it), and the bar flips
  // BELOW the selection when there is no room above the viewport top.
  const barW = barSize?.w ?? 440;
  const barH = barSize?.h ?? 76;
  const half = barW / 2;
  const vw = typeof window !== 'undefined' ? window.innerWidth : rect.left + half + 8;
  const minLeft = 8 + half;
  const maxLeft = Math.max(minLeft, vw - 8 - half);
  const flip = rect.top - barH - 8 < 0;
  const style: React.CSSProperties = {
    left: Math.min(Math.max(rect.left, minLeft), maxLeft),
    top: flip ? rect.bottom + 8 : rect.top - 8,
    transform: flip ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
  };

  const formatActive = (f: InlineFormat) => sel.activeFormats.includes(f);

  // Render the CONFIGURED actions: the first few as first-class buttons, the rest under "More".
  // Empty (host still loading) → a single Rewrite fallback so the bar is never actionless.
  const actionList = props.actions.length > 0 ? props.actions : FALLBACK_ACTIONS;
  const primaryActions = actionList.slice(0, PRIMARY_ACTION_COUNT);
  const overflowActions = actionList.slice(PRIMARY_ACTION_COUNT);
  const runAction = (a: SelectionActionItem) => {
    setRemindOpen(false);
    setMoreOpen(false);
    props.onAIAction(a.id, a.prompt);
  };

  return (
    <div
      ref={barRef}
      className="docit-bubblebar docit-bubblebar--rows"
      style={style}
      role="toolbar"
      aria-label="Selection actions"
    >
      {/* Row 1 — large, intuitive primary actions; AI is first-class, never behind a menu. */}
      <div className="docit-bubblebar__row docit-bubblebar__row--primary">
        {sel.canCommit && (
          <button
            type="button"
            className="docit-bubblebar__commit"
            aria-label="Commit selection"
            {...cmd(() => {
              setRemindOpen(false);
              props.onCommit();
            })}
          >
            Commit
          </button>
        )}

        {/* Stable busy slot: the pill holds the measured width of the three AI buttons. */}
        <span
          ref={aiGroupRef}
          className="docit-bubblebar__ai-group"
          style={props.aiBusy && aiGroupW !== null ? { width: aiGroupW, minWidth: aiGroupW } : undefined}
        >
          {props.aiBusy ? (
            <span className="docit-bubblebar__busy" role="status">
              ✦ Thinking…
            </span>
          ) : (
            <>
              {primaryActions.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="docit-ai-btn"
                  aria-label={a.title + ' with AI'}
                  title={a.title}
                  {...cmd(() => runAction(a))}
                >
                  ✦ {a.title}
                </button>
              ))}
              {overflowActions.length > 0 && (
                <button
                  type="button"
                  className="docit-ai-btn"
                  aria-haspopup="menu"
                  aria-expanded={moreOpen}
                  aria-label="More AI actions"
                  {...cmd(() => {
                    setRemindOpen(false);
                    setMoreOpen((o) => !o);
                  })}
                >
                  ✦ More ▾
                </button>
              )}
              <button
                type="button"
                className="docit-ai-btn"
                aria-label="Ask AI about this selection"
                {...cmd(() => {
                  setRemindOpen(false);
                  setMoreOpen(false);
                  props.onAskAI();
                })}
              >
                ✦ Ask AI
              </button>
            </>
          )}
        </span>

        <button
          type="button"
          aria-label="Start a task from this selection"
          {...cmd(() => {
            setRemindOpen(false);
            props.onStartTask();
          })}
        >
          + Task
        </button>
        <button
          type="button"
          aria-label="Taskize — extract tasks and decisions from this selection"
          title="Extract tasks & decisions"
          {...cmd(() => {
            setRemindOpen(false);
            setMoreOpen(false);
            props.onTaskize();
          })}
        >
          ✓ Taskize
        </button>
        <button
          type="button"
          aria-label="Remind me of this"
          aria-haspopup="dialog"
          aria-expanded={remindOpen}
          {...cmd(() => (remindOpen ? setRemindOpen(false) : openRemind()))}
        >
          ⏰ Remind
        </button>
      </div>

      {/* Row 2 — compact format cluster. */}
      <div className="docit-bubblebar__row docit-bubblebar__row--format">
        <button type="button" aria-label="Bold" data-active={formatActive('strong')} {...cmd(() => props.onToggleFormat('strong'))}>
          <b>B</b>
        </button>
        <button type="button" aria-label="Italic" data-active={formatActive('em')} {...cmd(() => props.onToggleFormat('em'))}>
          <i>I</i>
        </button>
        <button type="button" aria-label="Inline code" data-active={formatActive('code_inline')} {...cmd(() => props.onToggleFormat('code_inline'))}>
          <code>`</code>
        </button>

        <span className="docit-bubblebar__divider" aria-hidden="true" />

        <button type="button" aria-label="Heading 1" data-active={sel.blockType === 'heading1'} {...cmd(() => props.onSetBlockType('heading1'))}>
          H1
        </button>
        <button type="button" aria-label="Heading 2" data-active={sel.blockType === 'heading2'} {...cmd(() => props.onSetBlockType('heading2'))}>
          H2
        </button>
        <button type="button" aria-label="Heading 3" data-active={sel.blockType === 'heading3'} {...cmd(() => props.onSetBlockType('heading3'))}>
          H3
        </button>

        <span className="docit-bubblebar__divider" aria-hidden="true" />

        <button type="button" aria-label="Bulleted list" data-active={sel.listType === 'bullet_list'} {...cmd(() => props.onToggleList('bullet_list'))}>
          • list
        </button>
        <button type="button" aria-label="Numbered list" data-active={sel.listType === 'ordered_list'} {...cmd(() => props.onToggleList('ordered_list'))}>
          1. list
        </button>
        <button type="button" aria-label="Blockquote" data-active={sel.blockType === 'blockquote'} {...cmd(() => props.onSetBlockType('blockquote'))}>
          ❝ quote
        </button>
      </div>

      {moreOpen && overflowActions.length > 0 && (
        <div className="docit-bubblebar__popover docit-bubblebar__menu" role="menu" aria-label="More AI actions">
          {overflowActions.map((a) => (
            <button key={a.id} type="button" role="menuitem" className="docit-ai-btn" title={a.title} {...cmd(() => runAction(a))}>
              ✦ {a.title}
            </button>
          ))}
        </div>
      )}

      {remindOpen && (
        <div className="docit-bubblebar__popover" role="dialog" aria-label="Remind me">
          <input
            type="datetime-local"
            value={remindDue}
            aria-label="Reminder time"
            autoFocus
            onChange={(e) => setRemindDue(e.target.value)}
          />
          <input
            type="text"
            value={remindNote}
            placeholder="Note"
            aria-label="Reminder note"
            onChange={(e) => setRemindNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitRemind();
              }
            }}
          />
          <button type="button" disabled={!remindDue} onClick={submitRemind}>
            Save
          </button>
        </div>
      )}
    </div>
  );
}
